import {
    type GroupMember,
    type ListOfGroupItem,
    type ListOfGroupMember,
    type ListOfSocialGroup,
    type SocialGroup,
} from '@app/gen';

import { type WebsocketPodData } from '../../.././../store/types/websockets';
import { type DeepNullable } from '../../../../types';
import { type EmojiId } from '../emoji';
import { type PagingParams, type SortingDir } from '../requests';

export type ISODate = ReturnType<Date['toISOString']>;

export type GroupParam = {
    groupId?: string;
};

// TODO: Remove this invite if we are retiring the old invite process
export type InviteParam = {
    groupId: string;
    memberIds: string[];
};

export type UpdateInviteParam = {
    groupId: string;
    inviteId: string;
};

export type InviteListParam = {
    groupId: string;
};

export type Tier = 1 | 2 | 3 | 4;

/**
 * GroupItemNFT response can have missing object keys, null values, empty strings; Probably more....
 * Don't expect the response to have any of this data.
 */
export type GroupItemNFT = Partial<
    DeepNullable<{
        activity_date?: string;
        /** video src url */
        animation_url: string;
        attributes: unknown;
        average_price: number;
        banner_image: string;
        blockchain: string;
        category: unknown;
        collection_created_at: ISODate;
        collection_description: string;
        collection_image: string;
        collection_instagram_username: string;
        collection_name: string;
        collection_short_description: string;
        collection_twitter_username: string;
        collection_wiki_url: string;
        /** Collection id */
        contract_address: string;
        description: string;
        eager_transforms: unknown[];
        featured: boolean;
        floor_price: number;
        hidden: boolean;
        /** image src url */
        image: string;
        /** TODO: do we need to censor adult NFT images? */
        is_nsfw: boolean;
        item_count: number;
        market_cap: number;
        metadata_complete: boolean;
        mime_type: string | null;
        minter: boolean;
        name: string;
        num_owners: number;
        num_reports: number;
        one_day_average_price: number;
        one_day_change: number;
        one_day_sales: number;
        one_day_volume: number;
        owner_address: string;
        purchase_date: ISODate;
        purchase_price: number;
        sold_price: number;
        mint_date: ISODate;
        purchase_unit: string;
        seven_day_average_price: number;
        seven_day_change: number;
        seven_day_sales: number;
        seven_day_volume: number;
        /** TODO: resource_type - missing in response? */
        resource_type: unknown;
        size: unknown;
        thirty_day_average_price: number;
        thirty_day_change: number;
        thirty_day_sales: number;
        thirty_day_volume: number;
        token_id: string;
        token_type: string;
        token_uri: string;
        total_sales: number;
        total_supply: number;
        total_volume: number;
        verified: boolean;
        transfer_date: ISODate;
        slug: string;
    }>
>;
type G = NonNullable<SocialGroup['items']>[number];
export type GroupItem = Omit<G['item'], 'item_type'> & {
    item_type: {
        Nft: GroupItemNFT;
    };
    tier?: Tier;
};

export type GroupDataItem = Readonly<
    Omit<G, 'item'> & {
        item: GroupItem;
    }
>;

/**
 * TODO: @andykenward this should be generated from the schema swagger file but the response is not defined in that document yet.
 */
export interface GetGroupItemsResponse extends ListOfGroupItem {
    data: ReadonlyArray<GroupDataItem>;
    paging: PagingParams;
}

/**
 * Almost identical to `GetGroupItemsResponse` with the difference that data can be mutated
 * if needed.
 */
export interface MutableGetGroupItemsResponse extends ListOfGroupItem {
    data: GroupDataItem[];
    paging: PagingParams;
}

/**
 * TODO: @andykenward this should be generated from the schema swagger file but the response is not defined in that
 * document yet.
 */
export interface GetGroupMembersResponse extends ListOfGroupMember {
    data: GroupMember[]; // todo @shanimal see if it's ok to remove the readonly?
    paging: PagingParams;
}

export type GroupInviteUser = {
    id: string;
    email: string;
    yat: string;
    twitter_name?: string;
};

// TODO: Remove this invite if we are retiring the old invite process
export type GroupInvite = {
    id: string;
    inviter: GroupInviteUser;
    invitee: GroupInviteUser;
    status: string;
};

export type GetGroupInvitesResponse = GroupInvite[];

const ownershipFilters = ['Sold', 'Purchased', 'Minted', 'Transferred'] as const;
export type GroupItemOwnership = (typeof ownershipFilters)[number];
export const isGroupItemOwnership = (x: string): x is GroupItemOwnership =>
    ownershipFilters.includes(x as GroupItemOwnership);

export type GroupsFilters = 'following' | 'trending' | 'popular' | 'my_pods';

export type GroupsFilter = {
    value: GroupsFilters;
    label: string;
    disabled?: boolean;
    description?: string;
};

export interface PodsTab {
    label: string;
    value: GroupsFilters;
}

export type GroupFilterQuery = {
    spam?: boolean;
};

export type GroupItemSorts =
    | 'id'
    | 'user_id'
    | 'yat_page_id'
    | 'purchase_date'
    | 'purchase_price'
    | 'purchase_unit'
    | 'published_at'
    | 'created_at'
    | 'floor_price'
    | 'verified'
    | 'minter';

export type GroupItemsFilterQuery = {
    /** Array of collection contract_address */
    contract_addresses?: Array<string>;
    dir?: SortingDir;
    limit?: number;
    /** Array of member id */
    members?: Array<GroupMember['id']>;
    ownership?: GroupItemOwnership[];
    page?: number;
    sort?: GroupItemSorts;
    spam?: boolean;
};

export type GroupsItemsFilterQuery = {
    type?: GroupsFilters;
    ownership?: GroupItemOwnership[];
    min_floor_price?: string;
    max_floor_price?: string;
    contract_addresses?: Array<string>;
    spam?: boolean;
};

export type ChipFloorPrice = {
    keys: string[];
    label: string;
};

export type GroupFiltersCache = {
    [key: string]: boolean;
};
export type GroupOwnershipCache = { ownership?: GroupItemOwnership[] };
export type GroupCollectionsCache = { collections?: string[] };
export type GroupFloorPriceCache = { min_floor_price?: string; max_floor_price?: string };

export type GroupsCacheKey = Array<
    string | GroupOwnershipCache | GroupFiltersCache | GroupFloorPriceCache | GroupCollectionsCache
>;

export type GroupsFilterQuery = {
    limit?: number;
    page?: number;
    association?: string;
    spam?: boolean;
    user_filter?: boolean;
    type?: string;
};

export type SocialGroupTypeOverride = Omit<SocialGroup, 'items'> & {
    items: ReadonlyArray<GroupDataItem>;
};

export interface GetGroupsResponse extends ListOfSocialGroup {
    data: ReadonlyArray<SocialGroupTypeOverride>;
    paging: PagingParams;
}

export interface GroupId {
    group_id: string;
}

export interface GroupCanEditResponse extends GroupId {
    can_edit: boolean;
}

export type UseGroupsFilterParams = {
    filters: GroupsItemsFilterQuery | undefined;
    filtersExcDefault: GroupsItemsFilterQuery | undefined;
};

export interface GroupCanModerateResponse extends GroupId {
    moderation_level: string;
}
export type GroupCollection = Readonly<{
    collection_name: string;
    contract_address: string;
    collection_image: string;
    item_count: number;
    member_count: number;
}>;

export interface GetGroupCollectionsResponse {
    data: ReadonlyArray<GroupCollection>;
    paging: PagingParams;
}

export type GroupCollectionsFilterQuery = {
    spam?: boolean;
    page?: number;
    limit?: number;
    type?: string;
};

export enum EventTypes {
    FloorPrice = 'floor_price',
    Purchase = 'purchase',
    Mint = 'mint',
    Sold = 'sold',
}

export interface GroupEvent {
    data: GroupDataItem | WebsocketPodData;
    event: EventTypes;
}

export type CreateInviteParam = {
    group_id?: string;
};

export type ShowInviteParam = {
    inviteCode: string;
};

export type InviteUser = Omit<GroupInviteUser, 'email'>;

export type InviteGroupInformation = {
    id: string;
    assets_quantity: number;
    followers_quantity: number;
    header_image?: string;
    header_yat_page_item_id?: string;
    members_quantity: number;
    name: string;
    owner: {
        id: string;
        email?: string;
        twitter_name?: string;
        yat: EmojiId;
    };
    top_members: null[];
    invite_permission: SocialGroup['invite_permission'];
    ignore_member_limit: boolean;
};

export type CreateInviteData = Readonly<{
    id: string;
    invitation_acceptence_url: string;
    invite_type: {
        InviteCode: {
            code: string;
        };
    };
    group_information: InviteGroupInformation;
    invitee: null;
    inviter: InviteUser;
}>;

export enum InviteErrorCodes {
    UserIsAlreadyMember = 'UserIsAlreadyMember',
    AlreadyAccepted = 'AlreadyAccepted',
    InvitesLimitHasBeenReached = 'InvitesLimitHasBeenReached',
    GroupMembersLimitHasBeenReached = 'GroupMembersLimitHasBeenReached',
    InviteStatusPreventsThisAction = 'InviteStatusPreventsThisAction',
}

export type InviteAcceptabilityErrors = {
    type: InviteErrorCodes;
    status?: string;
    action?: string;
};

export type ShowInviteData = Readonly<{
    id: string;
    inviter: InviteUser;
    invitee: null | InviteUser;
    invite_type: {
        InviteCode: {
            code: string;
        };
    };
    group_information: InviteGroupInformation;
    acceptability_error?: InviteAcceptabilityErrors;
    code: string;
}>;

export type GetInviteDataResponse = {
    invites_generated: number;
    invites_accepted: number;
    invites_consumed: number;
    invite_limit: number;
};

export enum ImgSize {
    // todo do these sizes better?
    Large = 200,
    Medium = 186,
    Small = 80,
}
